import type { DeepReadonly } from "ts-essentials";

import { readState } from "@/__main__/app-state.mjs";
import { accountRefs } from "@/app/account.refs.mjs";
import { USER_ROLES } from "@/feature-auth/constants/constants.mjs";
import type { User } from "@/feature-auth/models/user-model.mjs";
import type { StubSubscription } from "@/feature-subscriber/utils/subscription.refs.mjs";
import { subscriptionRefs } from "@/feature-subscriber/utils/subscription.refs.mjs";
import type { PaymentPlan } from "@/feature-wallet/models/payment-plans.mjs";
import type { UserSubscription } from "@/feature-wallet/models/user-subscriptions.mjs";
import {
  hasSubscriptionError,
  useOngoingSubscriptions,
} from "@/feature-wallet/utils/subscription-status.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const SUBSCRIBER_ROLES = [
  USER_ROLES.subscriber,
  USER_ROLES.nonSubscriber,
  USER_ROLES.cryptoProSubscriber,
];

/**
 * Returns whether the given user is a subscriber. Generally speaking, {@link useIsSubscriber}
 * should be preferred unless you need to check the status of a user that is not the currently
 * logged in user.
 */
export function isUserSubscriber(
  user: DeepReadonly<Pick<User, "roles">>,
): boolean {
  return Boolean(
    user && user.roles?.some(({ code }) => SUBSCRIBER_ROLES.includes(code)),
  );
}

/**
 * Returns the currently logged in user's ongoing premium subscription if they have one.
 */
export function useSubscriberSubscription():
  | UserSubscription
  | StubSubscription
  | null {
  const { activeSubscription } = useSnapshot(subscriptionRefs);

  // TODO: handle non-premium subs
  const ongoingSubscriptions = useOngoingSubscriptions();

  return activeSubscription ?? ongoingSubscriptions[0] ?? null;
}

/**
 * Returns if the currently logged in user is a subscriber.
 */
export function useIsSubscriber(): boolean {
  const { isLoggedIn, activeSubscriber } = useSnapshot(accountRefs);

  return isLoggedIn && activeSubscriber;
}

/**
 * Returns the status of the currently logged in user's subscription.
 */
export function useSubscriberStatus(): UserSubscription["status"] {
  const subscription = useSubscriberSubscription();

  return subscription?.status ?? null;
}

/**
 * Returns `true` or `false` if the currently logged in user has a subscription error,
 * or `null` if the user has no ongoing subscription.
 */
export function useSubscriberError(): boolean {
  const subscription = useSubscriberSubscription();

  if (!subscription) return null;

  return hasSubscriptionError(subscription) ?? null;
}

/**
 * Returns the currently logged in user's subscription plan if they have one.
 */
export function useSubscriberPlan(): PaymentPlan | null {
  const subscription = useSubscriberSubscription();

  const {
    wallet: { paymentPlans },
  } = useSnapshot(readState);

  if (subscription) {
    if ("planName" in subscription) {
      return paymentPlans?.[subscription.planName] ?? null;
    }
  }

  return null;
}
